// Tiny fetch wrapper. Same-origin, cookie-based auth. Mirrors the
// pattern used by /root/tastytradeauto/frontend/src/api/client.ts —
// httpOnly cookie carries the session, /api endpoints are proxied
// to the rust backend in dev and served from the same vhost in prod.

const BASE = '/api';

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  if (res.status === 401) {
    throw new ApiError('Unauthorized', 401);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(text || `HTTP ${res.status}`, res.status);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json();
}

// Multipart/form-data POST. Used for file uploads (#14, #15). We must
// NOT set Content-Type ourselves — the browser sets it with the
// multipart boundary. Cookie auth still rides along via credentials.
// `path` may point outside /api (e.g. the unauthed `/u/<token>` upload
// route) — pass `{ absolute: true }` for those.
export async function apiForm<T>(
  path: string,
  form: FormData,
  opts: { absolute?: boolean } = {},
): Promise<T> {
  const url = opts.absolute ? path : `${BASE}${path}`;
  const res = await fetch(url, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  if (res.status === 401) {
    throw new ApiError('Unauthorized', 401);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(text || `HTTP ${res.status}`, res.status);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json();
}

export class ApiError extends Error {
  status: number;
  constructor(msg: string, status: number) {
    super(msg);
    this.status = status;
  }
}
